import { track } from "./analytics";

/** These attributes are authored by our views, never derived from DOM text or URLs. */
export const namedInteractions = {
	chatLink: "chat_link_clicked",
	attributeLink: "attribute_link_clicked",
	markdownLink: "markdown_link_clicked",
	captureProfileLink: "capture_profile_link_clicked",
	integrationsLink: "integrations_link_clicked",
	interactionDetails: "interaction_details_clicked",
	auditEventDetails: "audit_event_details_clicked",
} as const;
const names = new Set<string>(Object.values(namedInteractions));

/** Explicit native-control events and browser-handled navigation bypass Foldkit messages. */
export const installNamedInteractions = (
	root: Document,
	capture: typeof track = track,
): (() => void) => {
	const onClick = (event: Event) => {
		if (
			event.type === "auxclick" &&
			(!(event instanceof MouseEvent) || event.button !== 1)
		)
			return;
		const target = event.target;
		if (!(target instanceof Element)) return;
		const name = target
			.closest("[data-analytics-event]")
			?.getAttribute("data-analytics-event");
		if (name && names.has(name)) {
			capture(name, { surface: "app" });
			return;
		}
		// Foldkit intentionally leaves modified/new-tab/download links to the browser.
		const link = target.closest("a[href]");
		if (link?.hasAttribute("download") && !event.isTrusted) return;
		const linkTarget = link?.getAttribute("target");
		const modified =
			event instanceof MouseEvent &&
			(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
		if (
			link &&
			(event.type === "auxclick" ||
				modified ||
				(Boolean(linkTarget) && linkTarget !== "_self") ||
				link.hasAttribute("download"))
		) {
			capture("navigation_link_clicked", {
				surface: "app",
				kind: "browser_navigation",
			});
		}
	};
	root.addEventListener("click", onClick, true);
	root.addEventListener("auxclick", onClick, true);
	return () => {
		root.removeEventListener("click", onClick, true);
		root.removeEventListener("auxclick", onClick, true);
	};
};
